import moment from "moment";
import React, { useState } from "react";
import FilterToolbar from "../inputs/FilterToolbar";
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  Table,
  Timeline,
  TimelineBody,
  TimelineContent,
  TimelineItem,
  TimelinePoint,
  TimelineTime,
  TimelineTitle,
} from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN, CATEGORIES, LOAN } from "../../config/apiConfig";
import Drawer from "../Drawer";
import { formatToReal } from "../../utils/utils";
import {
  FaCheck,
  FaDollarSign,
  FaRegClock,
  FaRegEdit,
  FaRegEye,
} from "react-icons/fa";
import { IoMdClose, IoMdSearch } from "react-icons/io";
import QueryEditForm from "../forms/QueryEditForm";
import TableLoading from "../TableLoading";
import { Tooltip } from "react-tooltip";
import CreateQueryForm from "../forms/CreateQueryForm";
import EditQueryCustomForm from "../forms/EditQueryCustomForm";
import TitleStripe from "../common/TitleStripe";
import DrawerLoading from "../DrawerLoading";
import EmptyTable from "./EmptyTable";

const SimulationsTable = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const onPageChange = (page) => setCurrentPage(page);
  const [isVisibleFilter, setIsVisibleFilter] = useState(false);
  const [sortByPrice, setSortByPrice] = useState("");
  const [searchName, setSearchName] = useState("");
  const [nameParam, setNameParam] = useState("");
  const [isDrawerEditQuery, setIsDrawerEditQuery] = useState(false);
  const [isDrawerEditLoan, setIsDrawerEditLoan] = useState(false);
  const [isDrawerCreateSimulation, setIsDrawerCreateSimulation] =
    useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const rejectionOptions = [
    "Documento ilegível",
    "Documento cortado",
    "Documento fora do padrão",
    "Documento vencido",
    "Documento não corresponde ao tipo solicitado",
  ];

  const openRejectModal = (docId) => {
    setSelectedDocumentId(docId);
    setIsRejectModalOpen(true);
  };

  const confirmRejection = async () => {
    if (!rejectionReason) return alert("Selecione o motivo da rejeição.");
    try {
      await axios.post(`${LOAN.INVALIDATE_DOCUMENT}/${selectedDocumentId}`, {
        reason: rejectionReason,
      });
      await fetchLoanDetails(selectedLoan.identifier, selectedLoan.accessKey);
      setIsRejectModalOpen(false);
      setRejectionReason("");
    } catch (err) {
      console.error("Erro ao rejeitar documento:", err.message);
    }
  };

  const {
    isLoading: isLoadingLoans,
    error: errorLoans,
    data: loans,
  } = useQuery(["loans", currentPage, nameParam, sortByPrice], async () => {
    const { data } = await axios.get(
      `${LOAN.GET_ALL}?page=${currentPage}&searchName=${nameParam}&sortByPrice=${sortByPrice}`
    );
    setTotalPages(data.data.totalPages || 1);
    setTotalCount(data.data.totalCount || 0);
    return data.data.data;
  });

  const { isLoading: isLoadingDocumentTypes, data: documentTypes } = useQuery(
    ["documentTypes"],
    async () => {
      const { data } = await axios.get(`${LOAN.GET_DOCUMENT_TYPES}`);
      return data.data;
    }
  );

  const fetchLoanDetails = async (loanIdentifier, accessKey) => {
    const { data } = await axios.get(
      `${LOAN.GET_LOAN_BY_IDENTIFIER}/${loanIdentifier}?accessKey=${accessKey}`
    );
    setSelectedLoan(data.data);
  };

  const { data: categories } = useQuery(["categories"], async () => {
    const { data } = await axios.get(`${CATEGORIES.GET_ALL}`);
    return data.data.data;
  });

  const handleSearchName = (e) => {
    e.preventDefault();
    setNameParam(searchName);
  };

  const handleViewSimulation = async (loan) => {
    await fetchLoanDetails(loan.identifier, loan.accessKey);
    setIsOpenModal(true);
  };

  const handleEditSimulation = (query) => {
    setSelectedLoan(query);
    query?.queryCustomizedId
      ? setIsDrawerEditLoan(true)
      : setIsDrawerEditQuery(true);
  };

  const isUploaded = (docTypeId, partnerId = null) =>
    selectedLoan?.Documents?.some(
      (doc) =>
        doc.documentTypeId === docTypeId &&
        (doc.partnerId ?? null) === (partnerId ?? null)
    );

  const getDocsByAppliesTo = (appliesTo, partnerId = null) =>
    documentTypes
      ?.filter(
        (docType) =>
          docType.appliesTo === appliesTo || docType.appliesTo === "both"
      )
      ?.map((docType) => ({
        ...docType,
        uploaded: isUploaded(docType.id, partnerId),
      }));

  const getUploadedDoc = (docTypeId, partnerId = null) =>
    selectedLoan?.Documents?.find(
      (doc) =>
        doc.documentTypeId === docTypeId &&
        (doc.partnerId ?? null) === (partnerId ?? null)
    );

  const countDocs = (appliesTo, partnerId = null) => {
    const docs = getDocsByAppliesTo(appliesTo, partnerId);
    const total = docs.length;
    const enviados = docs.filter((doc) => doc.uploaded).length;
    return `${enviados}/${total}`;
  };

  return (
    <>
      <div className="flex flex-col md:flex-row justify-between mb-3 mt-2 items-end md:items-center gap-2">
        <FilterToolbar
          isVisible={isVisibleFilter}
          setIsVisible={setIsVisibleFilter}
        >
          <div className="flex gap-2">
            <div className="flex gap-2 flex-col md:flex-row w-full">
              <div className="flex items-center gap-2 w-full bg-gray-100 rounded-md pl-2 pr-0 py-1">
                <FaDollarSign className="color-primary text-xl mt-1" />
                <select
                  id="underline_select"
                  className="mt-1 block py-1 px-0 w-full md:w-40 text-sm text-gray-600 bg-transparent border-0 border-gray-300 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                  value={sortByPrice}
                  onChange={({ target }) => setSortByPrice(target.value)}
                >
                  <option selected value="">
                    Preço
                  </option>
                  <option value="DESC">Mais caros</option>
                  <option value="ASC">Mais baratos</option>
                </select>
              </div>
            </div>
          </div>
        </FilterToolbar>
        <Button
          className="primary"
          onClick={() => setIsDrawerCreateSimulation(true)}
        >
          Criar nova simulação
        </Button>
      </div>
      <form
        className="flex gap-1 mt-5 w-full pl-3 pr-2 py-2 md:py-1 rounded-md shadow-sm bg-gray-300"
        onSubmit={handleSearchName}
      >
        <input
          type="text"
          placeholder="Busque pelo cliente..."
          className="border-0 py-1 text-sm mt-1 focus:outline-none focus:ring-0 focus:border-gray-300 px-0 w-full bg-gray-300 placeholder:text-gray-700"
          onChange={(e) => setSearchName(e.target.value)}
          value={searchName}
        />
        <Button
          size="sm"
          className="flex items-center bg-secondary w-20"
          type="submit"
        >
          <IoMdSearch />
        </Button>
      </form>
      <small
        className="gap-2 items-center mt-5 mb-1 flex md:hidden justify-center"
        style={{ fontSize: 10 }}
      >
        <span>Deslize para o lado para visualizar todas as informações</span>{" "}
        <FaArrowsLeftRight />
      </small>
      {loans?.length > 0 ? (
        <div className="mt-0 md:mt-5 overflow-x-auto">
          <div className="min-w-full rounded-xl border border-[#e9e4df] bg-white shadow-sm overflow-hidden">
            <Table striped={false} className="text-xs xl:text-sm">
              {/* Cabeçalho sticky */}
              {loans?.length > 0 || isLoadingLoans ? (
                <Table.Head className="uppercase text-[11px] tracking-wide sticky top-0 z-10">
                  <Table.HeadCell className="text-center bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    ID
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    Razão Social
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    Cod. Acesso
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    Responsável
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    Data criação
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    Valor
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-[#f3efeb] text-[#6b5f57] font-semibold">
                    Ações
                  </Table.HeadCell>
                </Table.Head>
              ) : null}

              <Table.Body className="divide-y divide-gray-100">
                {!isLoadingLoans ? (
                  loans?.length > 0 ? (
                    loans.map((loan, index) => (
                      <Table.Row
                        key={index}
                        className={`transition ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } hover:bg-[#faf7f4]`}
                      >
                        {/* ID */}
                        <Table.Cell className="text-center font-medium text-gray-900 py-2">
                          #{loan.id}
                        </Table.Cell>

                        {/* Razão Social */}
                        <Table.Cell className="font-medium text-gray-900 py-2">
                          <span className="truncate block max-w-[220px]">
                            {loan?.Company?.name}
                          </span>
                        </Table.Cell>

                        {/* Código de acesso */}
                        <Table.Cell className="text-center font-medium text-gray-900 py-2">
                          {loan.accessKey}
                        </Table.Cell>

                        {/* Responsável */}
                        <Table.Cell className="text-center font-medium text-gray-900 py-2">
                          {loan?.Company?.responsibleName || "-"}
                        </Table.Cell>

                        {/* Data de criação */}
                        <Table.Cell className="text-center font-medium text-gray-900 py-2">
                          <div>
                            {moment(loan?.createdAt).format("DD/MM/yyyy")}
                          </div>
                          <Badge
                            color="gray"
                            className="mt-0.5 text-[11px] font-normal w-fit mx-auto"
                          >
                            {moment().diff(moment(loan?.createdAt), "days") ===
                            0
                              ? "Hoje"
                              : `${moment().diff(
                                  moment(loan?.createdAt),
                                  "days"
                                )} dias atrás`}
                          </Badge>
                        </Table.Cell>

                        {/* Valor */}
                        <Table.Cell className="text-center font-medium text-gray-900 py-2">
                          {formatToReal(loan?.amount)}
                        </Table.Cell>

                        {/* Ações */}
                        <Table.Cell className="text-center py-2">
                          <div className="flex items-center justify-center gap-2">
                            {/* Visualizar */}
                            <Tooltip
                              id="tt-view"
                              content="Visualizar Simulação"
                            />
                            <button
                              data-tooltip-id="tt-view"
                              onClick={() => handleViewSimulation(loan)}
                              className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition"
                            >
                              <FaRegEye size={16} />
                            </button>

                            {/* Editar */}
                            <Tooltip id="tt-edit" content="Editar Simulação" />
                            <button
                              data-tooltip-id="tt-edit"
                              onClick={() => handleEditSimulation(loan)}
                              className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition"
                            >
                              <FaRegEdit size={16} />
                            </button>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  ) : (
                    <Table.Row>
                      <Table.Cell
                        colSpan={7}
                        className="text-center py-6 text-gray-500"
                      >
                        Nenhuma simulação encontrada.
                      </Table.Cell>
                    </Table.Row>
                  )
                ) : (
                  <TableLoading cols={7} />
                )}
              </Table.Body>
            </Table>
          </div>
        </div>
      ) : (
        <EmptyTable icon="search_off">Nenhuma simulação encontrada</EmptyTable>
      )}

      {totalCount > 0 && (
        <div className="mt-3 text-center text-sm">
          Foram encontrados {totalCount} registros no total
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex overflow-x-auto justify-center mt-3">
          <Pagination
            layout="pagination"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            previousLabel=""
            nextLabel=""
            showIcons
          />
        </div>
      )}

      <Modal
        show={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        size="4xl"
      >
        <ModalHeader>Visualizar simulação</ModalHeader>
        <ModalBody className="relative min-h-40">
          {!selectedLoan || !documentTypes ? (
            <DrawerLoading />
          ) : (
            <div className="flex flex-col gap-1">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 bg-gray-200 p-2 -mb-2 text-sm">
                <a
                  className="underline"
                  href={`https://app.ampliacapital.com.br/form/${selectedLoan?.identifier}`}
                >
                  Link do fomulário do cliente
                </a>
                <div>
                  Código de acesso:{" "}
                  <span className="font-semibold">
                    {selectedLoan?.accessKey}
                  </span>
                </div>
                <div>
                  Data de criação:{" "}
                  {moment(selectedLoan?.createdAt).format("DD/MM/yyyy")}
                </div>
              </div>
              <TitleStripe className="mb-2">Informações da empresa</TitleStripe>
              <div className="shadow-sm rounded-md p-2 border border-gray-200 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="flex gap-2 items-center">
                    <div className="font-semibold">Empresa:</div>
                    <div>{selectedLoan?.Company?.name}</div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="font-semibold">CNPJ:</div>
                    <div>{selectedLoan?.Company?.document}</div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="font-semibold">Telefone:</div>
                    <a
                      target="_blank"
                      className="underline"
                      href={`https://wa.me/55${selectedLoan?.Company?.phoneNumber.replace(
                        /\D/g,
                        ""
                      )}`}
                    >
                      {selectedLoan?.Company?.phoneNumber}
                    </a>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="font-semibold">Email:</div>
                    <a
                      className="underline"
                      href={`mailto:${selectedLoan?.Company?.email}`}
                    >
                      {selectedLoan?.Company?.email}
                    </a>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="font-semibold">Endereço:</div>
                    <div>
                      {selectedLoan?.Company?.address},{" "}
                      {selectedLoan?.Company?.addressNumber}
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="font-semibold">Bairro:</div>
                    <div>{selectedLoan?.Company?.addressNeighborhood}</div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="font-semibold">Cidade:</div>
                    <div>{selectedLoan?.Company?.addressCity}</div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="font-semibold">CEP:</div>
                    <div>{selectedLoan?.Company?.cep}</div>
                  </div>
                </div>
              </div>
              <TitleStripe className="mb-2">Informações do crédito</TitleStripe>
              <div className="shadow-sm rounded-md p-2 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="flex gap-2 items-center">
                    <div className="font-semibold">Valor desejado:</div>
                    <Badge className="w-fit" size="lg">
                      {formatToReal(selectedLoan?.amount)}
                    </Badge>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="font-semibold">Nº de Parcelas:</div>
                    <div>{selectedLoan?.installments}</div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="font-semibold">Segmento do crédito:</div>
                    <div>
                      {selectedLoan?.SegmentType?.name || "Não informado"}
                    </div>
                  </div>
                </div>
              </div>
              <TitleStripe className="mb-2">
                Documentos da empresa ({countDocs("company")})
              </TitleStripe>
              <div className="shadow-sm rounded-md p-2 border border-gray-200">
                <Timeline>
                  {getDocsByAppliesTo("company")?.map((doc) => (
                    <TimelineItem key={doc.id}>
                      <TimelinePoint />
                      <TimelineContent>
                        <TimelineTime
                          className={`font-semibold flex gap-1 items-center pt-1.5
                            ${
                              doc.uploaded
                                ? "text-green-600"
                                : "text-yellow-500"
                            }
                          `}
                        >
                          {doc.uploaded ? (
                            <div className="flex items-center gap-1">
                              <FaCheck fontSize={12} />
                              <span>Enviado</span>
                              {getUploadedDoc(doc.id)?.rejectionReason && (
                                <span
                                  color="failure"
                                  className="text-red-600 text-2xs italic"
                                >
                                  {" "}
                                  - (Rejeitado:{" "}
                                  {getUploadedDoc(doc.id).rejectionReason})
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <FaRegClock fontSize={12} />
                              <span>Pendente</span>
                            </div>
                          )}
                        </TimelineTime>
                        <TimelineTitle className="text-base">
                          {doc.name}
                        </TimelineTitle>
                        <TimelineBody className="text-sm">
                          {doc.uploaded && (
                            <div className="flex gap-2 items-center">
                              <a
                                href={getUploadedDoc(doc.id)?.fileUrl}
                                className="underline"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FaRegEye className="inline-block mr-1" />
                                Visualizar documento
                              </a>
                              {!getUploadedDoc(doc.id)?.rejectionReason && (
                                <button
                                  type="button"
                                  className="text-red-600 underline"
                                  onClick={() =>
                                    openRejectModal(getUploadedDoc(doc.id)?.id)
                                  }
                                >
                                  <IoMdClose className="inline-block" />
                                  Rejeitar documento
                                </button>
                              )}
                            </div>
                          )}
                        </TimelineBody>
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              </div>
              <div>
                {selectedLoan?.Company?.Partners?.map((partner) => (
                  <div key={partner.id}>
                    <TitleStripe className="mb-3">
                      Documentos de {partner?.name?.toLowerCase()} (
                      {countDocs("partner", partner.id)})
                    </TitleStripe>
                    <div className="shadow-sm rounded-md p-2 border border-gray-200">
                      <Timeline>
                        {getDocsByAppliesTo("partner", partner.id)?.map(
                          (doc) => (
                            <TimelineItem key={`${doc.id}-${partner.id}`}>
                              <TimelinePoint />
                              <TimelineContent>
                                <TimelineTime
                                  className={`font-semibold flex gap-1 items-center pt-1.5 
                                    ${
                                      doc.uploaded
                                        ? "text-green-600"
                                        : "text-yellow-500"
                                    }
                                  `}
                                >
                                  {doc.uploaded ? (
                                    <div className="flex items-center gap-1">
                                      <FaCheck fontSize={12} />
                                      <span>Enviado</span>
                                      {getUploadedDoc(doc.id, partner.id)
                                        ?.rejectionReason && (
                                        <span
                                          color="failure"
                                          className="text-red-600 text-2xs italic"
                                        >
                                          {" "}
                                          - (Rejeitado:{" "}
                                          {
                                            getUploadedDoc(doc.id, partner.id)
                                              .rejectionReason
                                          }
                                          )
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <FaRegClock fontSize={12} />
                                      <span>Pendente</span>
                                    </div>
                                  )}
                                </TimelineTime>
                                <TimelineTitle className="text-base">
                                  {doc.name}
                                </TimelineTitle>
                                <TimelineBody className="text-sm">
                                  {doc.uploaded && (
                                    <div className="flex gap-2 items-center">
                                      <a
                                        href={
                                          getUploadedDoc(doc.id, partner.id)
                                            ?.fileUrl
                                        }
                                        className="underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <FaRegEye className="inline-block mr-1" />
                                        Visualizar documento
                                      </a>
                                      {!getUploadedDoc(doc.id, partner.id)
                                        ?.rejectionReason && (
                                        <button
                                          type="button"
                                          className="text-red-600 underline"
                                          onClick={() =>
                                            openRejectModal(
                                              getUploadedDoc(doc.id, partner.id)
                                                ?.id
                                            )
                                          }
                                        >
                                          <IoMdClose className="inline-block" />
                                          Rejeitar documento
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </TimelineBody>
                              </TimelineContent>
                            </TimelineItem>
                          )
                        )}
                      </Timeline>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="flex justify-center">
          <Button onClick={() => setIsOpenModal(false)}>Fechar</Button>
        </ModalFooter>
      </Modal>

      <Modal
        show={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        size="md"
        dismissible
      >
        <ModalHeader>Rejeitar Documento</ModalHeader>
        <ModalBody>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo da rejeição
            </label>
            <select
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            >
              <option value="">Selecione um motivo</option>
              {rejectionOptions.map((reason, i) => (
                <option key={i} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={confirmRejection}>Confirmar Rejeição</Button>
          <Button color="gray" onClick={() => setIsRejectModalOpen(false)}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      <Drawer
        title={`Editar Simulação`}
        onClose={setIsDrawerEditLoan}
        isOpen={isDrawerEditLoan}
        className={"w-full md:w-4/6 lg:w-3/6 xl:w-2/6"}
      >
        <EditQueryCustomForm
          categories={categories}
          onClose={setIsDrawerEditLoan}
          fetchQueries={() => {
            queryClient.invalidateQueries(["loans", currentPage]);
          }}
          query={selectedLoan}
          isOpen={isDrawerEditLoan}
        />
      </Drawer>

      <Drawer
        title={`Criar nova simulação`}
        onClose={setIsDrawerCreateSimulation}
        isOpen={isDrawerCreateSimulation}
        className={"w-full md:w-4/6 lg:w-3/6 xl:w-2/6"}
      >
        <CreateQueryForm
          categories={categories}
          onClose={setIsDrawerCreateSimulation}
          fetchSimulations={() => {
            queryClient.invalidateQueries(["loans", currentPage]);
          }}
          isOpen={isDrawerCreateSimulation}
        />
      </Drawer>
    </>
  );
};

export default SimulationsTable;
